import React from "react";
import { Link } from "react-router-dom";
import { Shield, Github, MessageSquare, Youtube, Facebook, Twitter, Send, Globe } from "lucide-react";
import { Input } from "./ui/input";
import { toast } from "sonner";
import { useContent } from "../context/ContentContext";
import { useContentObject } from "../hooks/useContentData";
import { contentFallback } from "../contentDefaults";

const SOCIAL_ICONS = { discord: MessageSquare, youtube: Youtube, facebook: Facebook, twitter: Twitter, github: Github };

const FALLBACK_SOCIAL = [
  { platform: "discord", url: "https://discord.gg/aetheris" },
];
const FALLBACK_COLS = [
  { title: "Servidor", links: [["/wiki","Server Info"],["/news","Noticias"],["/rankings","Rankings"],["/downloads","Descargas"]].map(([url,label])=>({url,label})) },
  { title: "Comunidad", links: [{label:"Discord",url:"#"},{label:"Forum",url:"#"},{label:"Wiki",url:"#"},{label:"Reglas",url:"#"},{label:"Soporte",url:"#"}] },
];

const Footer = () => {
  const { value: aboutText } = useContent("footer.about", "Un servidor Interlude reimaginado...");
  const { value: socialLinks } = useContent("footer.social", FALLBACK_SOCIAL);
  const { value: columns } = useContent("footer.columns", FALLBACK_COLS);
  const { value: copyright } = useContent("footer.copyright", "L2 AETHERIS · No afiliado con NCSoft.");
  const { data: brand } = useContentObject("footer.brand", contentFallback("footer.brand"));
  const { value: legalPages } = useContent("legal.pages", []);

  const cols = Array.isArray(columns) && columns.length ? columns : FALLBACK_COLS;
  const social = Array.isArray(socialLinks) && socialLinks.length ? socialLinks : FALLBACK_SOCIAL;
  const legalLinks = (Array.isArray(legalPages) ? legalPages : []).filter((p) => p && p.slug && p.show_in_footer !== false);

  const subscribe = (e) => {
    e.preventDefault();
    const data = new FormData(e.currentTarget);
    const email = data.get("email");
    if (!email) return;
    toast.success("Suscrito", { description: "Recibirás las novedades del Reino en tu email." });
    e.currentTarget.reset();
  };

  return (
    <footer className="relative bg-[#08080b] border-t border-[rgba(212,168,67,0.15)] overflow-hidden">
      <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[60%] h-px bg-gradient-to-r from-transparent via-[#d4a843] to-transparent opacity-50" />

      <div className="max-w-7xl mx-auto px-6 md:px-10 py-16">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          {/* Brand */}
          <div className="md:col-span-4">
            <Link to="/" className="flex items-center gap-3">
              <div className="relative w-11 h-11">
                <div className="absolute inset-0 rounded-full bg-gradient-to-br from-[#f5c542] via-[#d4a843] to-[#8b6914]" />
                <div className="absolute inset-[3px] rounded-full bg-[#0a0a0d] flex items-center justify-center">
                  <Shield className="w-5 h-5 text-[#d4a843]" />
                </div>
              </div>
              <div>
                <div className="font-display font-extrabold text-lg tracking-[0.18em] text-[#f0ebe0]">{brand?.name || "L2 AETHERIS"}</div>
                <div className="font-mono text-[9px] tracking-[0.4em] text-[#d4a843] uppercase">{brand?.tagline || "Interlude · Premium"}</div>
              </div>
            </Link>
            <p className="mt-5 text-sm text-[#b8b3a8] leading-relaxed max-w-sm whitespace-pre-line">
              {aboutText}
            </p>
            <div className="flex items-center gap-2 mt-6">
              {social.map((s) => {
                const Icon = SOCIAL_ICONS[s.platform] || Globe;
                return (
                  <a
                    key={s.platform + s.url}
                    href={s.url}
                    target="_blank" rel="noreferrer"
                    className="w-9 h-9 rounded-sm bg-[#14141a] border border-[rgba(212,168,67,0.15)] flex items-center justify-center text-[#b8b3a8] hover:text-[#d4a843] hover:border-[rgba(212,168,67,0.5)] transition-all"
                    aria-label={s.platform}
                  >
                    <Icon className="w-4 h-4" />
                  </a>
                );
              })}
            </div>
          </div>

          {/* Columns */}
          {cols.slice(0, 2).map((col, i) => (
            <div key={col.title + i} className="md:col-span-2">
              <h4 className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] mb-4">{col.title}</h4>
              <ul className="space-y-2.5 text-sm text-[#b8b3a8]">
                {(col.links || []).map((l, j) => {
                  const isExt = /^https?:|^mailto:/i.test(l.url);
                  return (
                    <li key={l.label + j}>
                      {isExt ? (
                        <a href={l.url} target="_blank" rel="noreferrer" className="hover:text-[#d4a843] transition-colors">{l.label}</a>
                      ) : (
                        <Link to={l.url} className="hover:text-[#d4a843] transition-colors">{l.label}</Link>
                      )}
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}

          {/* Newsletter */}
          <div className="md:col-span-4">
            <h4 className="font-display tracking-[0.18em] uppercase text-xs text-[#d4a843] mb-4">Newsletter</h4>
            <p className="text-sm text-[#b8b3a8] mb-4">
              Recibe avisos del Grand Opening, eventos y patch notes directo en tu email.
            </p>
            <form onSubmit={subscribe} className="flex gap-2">
              <Input
                name="email"
                type="email"
                required
                placeholder="tu@correo.com"
                className="flex-1 bg-[#14141a] border-[rgba(212,168,67,0.2)] text-[#f0ebe0] focus:border-[#d4a843] rounded-sm font-mono text-sm"
              />
              <button type="submit" className="btn-gold px-4 rounded-sm" aria-label="Suscribirse">
                <Send className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-[rgba(212,168,67,0.1)] flex flex-col md:flex-row items-center justify-between gap-4 text-xs text-[#6f6c66]">
          <div className="font-mono">
            © {new Date().getFullYear()} {copyright}
          </div>
          <div className="flex items-center gap-5 font-mono">
            {legalLinks.map((p, i) => (
              <Link key={p.slug + i} to={`/p/${p.slug}`} className="hover:text-[#d4a843] transition-colors">
                {p.footer_label || p.title}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
